import Vue from 'vue'
import '@/styles/styles.scss'
import { firebaseAuth, firebaseAuthPersistenceReady } from '@/firebase'
import axios from '@/utils/axios'
import useAuth from '@/composables/useAuth'
import useSnackbar, { SnackbarType } from '@/composables/snackbar'
import App from './App.vue'
import './plugins/vee-validate'
import './plugins/autoload-components'
import vuetify from './plugins/vuetify'
import router from './router'

Vue.config.productionTip = false

let vm = null
let tokenRefreshPromise = null
let connectionWarningVisible = false
const {
  state: auth, getValidToken, refreshToken, logout,
} = useAuth()
const { showSnackbar } = useSnackbar()

const NETWORK_RETRY_DELAYS = [500, 1500]

const isNetworkError = error => !error.response

const wait = delay => new Promise(resolve => setTimeout(resolve, delay))

const toCompanyIds = companyId => {
  if (Array.isArray(companyId)) return companyId

  return companyId ? [companyId] : []
}

const showConnectionWarning = () => {
  if (connectionWarningVisible) return

  connectionWarningVisible = true
  showSnackbar('Connection lost. Your session is still active and requests will retry when the connection returns.', SnackbarType.INFO)
}

const refreshTokenOnce = () => {
  if (!tokenRefreshPromise) {
    tokenRefreshPromise = refreshToken().finally(() => {
      tokenRefreshPromise = null
    })
  }

  return tokenRefreshPromise
}

const isPublicRoute = routeName => ['login', 'logout', 'pending-approval', 'no-academy'].includes(routeName)

const validateAuthenticatedUser = async user => {
  if (!user || !vm) return

  const { hasRole } = require('@/composables/useAuth').default()
  const { getUserAcademy } = require('@/composables/api/academies').default()

  try {
    const userData = await getUserAcademy()
    if (userData.status && userData.status !== 'APPROVED' && !isPublicRoute(router.currentRoute.name)) {
      await router.push({ name: 'pending-approval' })

      return
    }

    if (hasRole(['COACH'])) {
      const userCompanyIds = toCompanyIds(userData.companyId)
      if (!userCompanyIds.some(id => id) && !isPublicRoute(router.currentRoute.name)) {
        await router.push({ name: 'no-academy' })
      }
    }
  } catch (error) {
    // An unavailable profile service does not prove that the Coach has no academy.
    console.warn('Unable to validate user access while offline:', error)
  }
}

function setupAxios() {
  axios.defaults.baseURL = process.env.VUE_APP_API_BASE_URL

  axios.interceptors.request.use(
    async config => {
      const token = await getValidToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new Event('api-activity'))
        }
      } else {
        delete config.headers.Authorization
      }

      return config
    },
    error => Promise.reject(error),
  )

  axios.interceptors.response.use(
    response => response,
    async error => {
      const config = error.config || {}
      if (isNetworkError(error)) {
        showConnectionWarning()
        const retryCount = config.p72NetworkRetryCount || 0
        if (config.method?.toLowerCase() === 'get' && retryCount < NETWORK_RETRY_DELAYS.length) {
          config.p72NetworkRetryCount = retryCount + 1
          await wait(NETWORK_RETRY_DELAYS[retryCount])

          return axios(config)
        }
      } else if (error.response.status === 401) {
        if (auth.user && !config.p72TokenRefreshAttempted) {
          config.p72TokenRefreshAttempted = true

          try {
            const newToken = await refreshTokenOnce()
            if (newToken) {
              config.headers = {
                ...config.headers,
                Authorization: `Bearer ${newToken}`,
              }
              config.p72RefreshSucceeded = true

              return axios(config)
            }
          } catch (refreshError) {
            if (isNetworkError(refreshError)) {
              showConnectionWarning()

              return Promise.reject(error)
            }

            console.error('Token refresh failed:', refreshError)
          }
        }

        // Only a repeated 401 after a successful forced refresh proves the session is no longer valid.
        if (config.p72RefreshSucceeded && !auth.isLoggingOut) {
          await logout()
          if (router.currentRoute.name !== 'login' && router.currentRoute.name !== 'logout') {
            router.push({ name: 'login' })
          }
        }
      } else if (error.response && error.response.status === 403) {
        if (router.currentRoute.name !== 'error-403') {
          router.push({ name: 'error-403' })
        }
      }

      return Promise.reject(error)
    },
  )
}

setupAxios()

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    connectionWarningVisible = false
    showSnackbar('Connection restored. Retrying account access checks.', SnackbarType.SUCCESS)
    validateAuthenticatedUser(auth.user)
  })
}

firebaseAuthPersistenceReady.finally(() => firebaseAuth.onIdTokenChanged(async user => {
  // Handle logout only if user was previously logged in and now isn't
  if (vm && auth.user && !user && !auth.isLoggingOut) {
    console.log('User token revoked or expired')
    auth.user = null
    auth.jwtData = null
    auth.lastTokenRefresh = null

    if (router.currentRoute.name !== 'login' && router.currentRoute.name !== 'logout') {
      await router.push({ name: 'login' })
    }

    return null
  }

  // Update auth state with new user data
  if (user) {
    auth.user = user
    try {
      auth.jwtData = await user.getIdTokenResult()
      auth.lastTokenRefresh = Date.now()
    } catch (error) {
      console.warn('Unable to read the latest Firebase token claims:', error)
    }
  }

  if (!vm) {
    vm = new Vue({
      router,
      vuetify,
      render: h => h(App),
    }).$mount('#app')
  }

  await validateAuthenticatedUser(user)

  return null
}))
